import {
  BountiesEventSchema,
  type BountiesEvent,
  BountyClaimedSchema,
  BountyRejectedSchema,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { cue } from '../audio/cues.js';
import { BountyPanel } from './bounty-panel.js';
import { bountyClaimedText, bountyRefusalText } from './bounty-text.js';

/** The little of a Colyseus room this needs. */
interface BountyRoom {
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
  send: (type: string, payload: unknown) => void;
}

export interface BountyClientOptions {
  room: BountyRoom;
  icons: () => CssAtlas | null;
  /** A line of the chat from the game, apart from what players say. */
  notice: (text: string) => void;
  /** A refusal over the world, gone by itself. */
  refuse: (text: string) => void;
}

/**
 * The client's side of the wanted boards (ADR 0096): the board's window,
 * and the place of the beast the player hunts, from the last `bounties` the
 * server sent (it keeps sending them while the board is in mind), for the
 * maps. It decides nothing: reach, level and payment are the server's.
 */
export class BountyClient {
  private readonly panel: BountyPanel;
  private last: BountiesEvent | undefined;
  /** Bumped at each `bounties`, so the maps redraw only when it moved. */
  private version = 0;

  constructor(private readonly options: BountyClientOptions) {
    const { room } = options;
    this.panel = new BountyPanel({
      icons: options.icons,
      onTake: (boardId, slot) => {
        room.send('bounty_take', { boardId, slot });
      },
      onClaim: (boardId, slot) => {
        room.send('bounty_claim', { boardId, slot });
      },
    });
    room.onMessage('bounties', (payload: unknown) => {
      const parsed = BountiesEventSchema.safeParse(payload);
      if (!parsed.success) return;
      this.last = parsed.data;
      this.version += 1;
      this.panel.update(parsed.data);
    });
    room.onMessage('bounty_claimed', (payload: unknown) => {
      const parsed = BountyClaimedSchema.safeParse(payload);
      if (!parsed.success) return;
      options.notice(bountyClaimedText(parsed.data));
      if (this.panel.isOpen) this.panel.showClaimed(parsed.data);
    });
    room.onMessage('bounty_rejected', (payload: unknown) => {
      const parsed = BountyRejectedSchema.safeParse(payload);
      if (!parsed.success) return;
      cue('sfx.error');
      // The board's window tells its own refusals; out of it they go over the world.
      if (this.panel.isOpen) this.panel.showRejected(parsed.data);
      else options.refuse(bountyRefusalText(parsed.data, this.last?.biome));
    });
  }

  /** Changes whenever the server told something new of the bounties. */
  get stamp(): number {
    return this.version;
  }

  /** The place of the beast called out for this player, while it stands. */
  get target(): { slot: string; x: number; y: number } | undefined {
    const out = this.last?.bounties.find((bounty) => bounty.state === 'out');
    return out === undefined ? undefined : { slot: out.slot, x: out.x, y: out.y };
  }

  get isOpen(): boolean {
    return this.panel.isOpen;
  }

  /** The board whose window is open. */
  get boardId(): string | undefined {
    return this.panel.boardId;
  }

  /** Open a board's window and ask for its bounties. */
  open(boardId: string): void {
    this.panel.open(boardId);
    this.options.room.send('bounties', { boardId });
  }

  close(): void {
    this.panel.close();
  }

  destroy(): void {
    this.panel.destroy();
  }
}
